import axios from 'utils/bsac-api.js';
import conversions from 'utils/conversions.js';
import defaultValues from 'constant/shopping-order/default-values';
import environments from 'constant/environments.js';
import cartItemDefaultValues from 'constant/shopping-cart-item/default-values';

const state = () => Object.assign({}, defaultValues);

const getters = {
  /**
   * お客様情報を取得する
   * @param {*} state
   * @returns
   */
  applicant: (state) => state.applicant,
  /**
   * 発送手数料を取得する
   * @param {*} state
   */
  carriage: (state, getters) => {
    return state.getCarriage();
  },
  /**
   * 発送手数料（日本円表記）を取得する
   * @param {*} state
   */
  carriageJpy: (state, getters) => {
    return conversions.toJpy(state.getCarriage());
  },
  /**
   * カートの商品リストを取得する
   * @param {*} state
   * @returns
   */
  cartItems: (state, getters, rootState, rootGetters) => {
    state.cartItems.forEach((cartItem) => {
      // 最新の商品情報に置き換える
      const product = rootGetters['product/product'](cartItem.productId);
      // console.log(`product:${product}`);
      cartItem.product = product;
      if (cartItem.product) {
        // 小計を計算する
        let subTotal = parseInt(cartItem.product.sellingPrice) * parseInt(cartItem.quantity);
        if (isNaN(subTotal)) subTotal = 0;
        cartItem.subTotal = subTotal;
        cartItem.subTotalJpy = `¥${subTotal.toLocaleString()}`;
      }
    });
    // 商品情報が存在するカートのみ返す
    state.cartItems = state.cartItems.filter((item) => {
      return item.product !== null;
    });
    return state.cartItems;
  },
  /**
   * お届け先情報を取得する
   * @param {*} state
   * @returns
   */
  delivery: (state) => state.delivery,
  /**
   * 入力概要を取得する
   * @param {*} getters
   * @returns
   */
  inputSummary: (state, getters, rootState, rootGetters) => {
    return state.getInputSummary();
  },
  /**
   * 登録用リクエストボディを生成する
   * @param {*} state
   * @param {*} getters
   * @param {*} rootState
   * @param {*} rootGetters
   */
  requestBodyForRegister: (state, getters, rootState, rootGetters) => {
    return state.getRequestBodyForRegister();
  },
  /**
   * 登録レスポンス内の支払い方法を取得する
   * @param {*} state
   * @returns
   */
  payByInRegisterResponse: (state) => {
    return state.registerResponse.payBy;
  },
  /**
   * 支払い情報を取得する
   * @param {*} state
   * @returns
   */
  payment: (state) => state.payment,
  /**
   * 登録レスポンス内のリダイレクトURLを取得する
   * @param {*} state
   * @returns
   */
  redirectUrlInRegisterResponse: (state) => {
    return state.registerResponse.redirectUrl;
  },
  /**
   * 登録レスポンス内のステータスコードから登録結果（true/false）を取得する
   * @param {*} state
   * @returns
   */
  resultInRegisterResponse: (state) => {
    return state.registerResponse.status === 201;
  },
  /**
   * 商品の合計額を取得する
   * @param {*} getters
   * @returns
   */
  totalPriceOfProducts: (state) => {
    return state.getTotalPriceOfProducts();
  },
  /**
   * カートの合計額を取得する
   * @param {*} state
   * @returns
   */
  totalPriceWithTax: (state, getters) => {
    return state.getTotalPriceWithTax();
  },
  /**
   * カートの合計額（日本円表記）を取得する
   * @param {*} getters
   * @returns
   */
  totalPriceWithTaxJpy: (state, getters) => {
    return conversions.toJpy(state.getTotalPriceWithTax());
  },
};

const mutations = {
  /**
   * カートに商品を追加する
   * 同じ商品が追加済みの場合は数量を加算する
   * @param {*} state
   * @param {*} param1
   */
  addProductInCart(state, { product, quantity }) {
    let cartItems = state.cartItems.filter((item) => {
      return item.productId === product.id;
    });
    if (cartItems.length < 1) {
      let cartItem = Object.assign({}, cartItemDefaultValues.cartItem);

      cartItem.productId = product.id ? product.id : null;
      cartItem.product = product ? product : null;
      cartItem.quantity = quantity ? parseInt(quantity) : 0;

      state.cartItems.push(cartItem);
    } else {
      cartItems[0].quantity = parseInt(cartItems[0].quantity) + parseInt(quantity);
    }
  },
  /**
   * カートから商品を削除する
   * @param {*} state
   * @param {*} productId
   */
  deleteCartItem(state, { productId }) {
    state.cartItems = state.cartItems.filter((item) => {
      return item.productId !== productId;
    });
  },
  /**
   * ストアを初期化する
   * @param {*} state
   */
  reset(state) {
    state.applicant = Object.assign({}, defaultValues.applicant);
    state.cartItems = Object.assign([], defaultValues.cartItems);
    state.delivery = Object.assign({}, defaultValues.delivery);
    state.payment = Object.assign({}, defaultValues.payment);
  },
  /**
   * テスト用データをセットする
   * @param {*} state
   */
  resetForTest(state) {
    state.applicant = Object.assign({}, defaultValues.applicant);
    state.cartItems = Object.assign([], defaultValues.cartItems);
    state.delivery = Object.assign({}, defaultValues.delivery);
    state.payment = Object.assign({}, defaultValues.payment);
  },
  /**
   * お客様情報をセットする
   * @param {*} state
   * @param {*} applicant
   */
  setApplicant(state, applicant) {
    state.applicant = applicant;
  },
  /**
   * パートナー情報から申込者内容をセットする
   * @param {*} state
   * @param {*} partner
   */
  setApplicationUsingPartner(state, partner) {
    if (!partner) return;

    let applicant = Object.assign({}, defaultValues.applicant);

    applicant.address1st = partner.address1st ? partner.address1st : null;
    applicant.address2nd = partner.address2nd ? partner.address2nd : null;
    applicant.email = partner.email ? partner.email : null;
    applicant.name = partner.nameJa ? partner.nameJa : null;
    applicant.nameKana = partner.nameKana ? partner.nameKana : null;
    applicant.partnerUserId = partner.id ? partner.id : null;
    applicant.postCode = partner.postCode ? partner.postCode : null;
    applicant.prefecture = partner.prefecture ? partner.prefecture : null;
    applicant.tel = partner.phoneNo ? partner.phoneNo : null;

    state.applicant = applicant;
    // console.debug(JSON.stringify(state.applicant));
  },
  /**
   * お届け先情報をセットする
   * @param {*} state
   * @param {*} delivery
   */
  setDelivery(state, delivery) {
    state.delivery = delivery;
  },
  /**
   * お支払方法をセットする
   * @param {*} state
   * @param {*} payment
   */
  setPayment(state, payment) {
    if (payment && (payment.payBy === 'A' || payment.payBy === 'C')) {
      payment.payAtDay = '';
      payment.payAtMonth = '';
      payment.payAtYear = '';
    }
    state.payment = payment;
  },
  /**
   * 登録時のレスポンスをセットする
   * @param {*} state
   * @param {*} response
   */
  setRegisterResponse(state, response) {
    // console.debug(`setRegisterResponse: ${JSON.stringify(response)}`);
    if (!response) return;
    state.registerResponse.status = response.status;
    state.registerResponse.message = response.data.message;
    state.registerResponse.payBy = response.data.pay_by;
    state.registerResponse.redirectUrl = response.data.redirect_url;
    // console.debug(`state.registerResponse:${JSON.stringify(state.registerResponse)}`);
  },
  /**
   * 数量を更新する
   * @param {*} state
   * @param {*} param1
   */
  updateQuantity(state, { productId, quantity }) {
    state.cartItems.forEach((cartItem) => {
      if (cartItem.productId === productId) cartItem.quantity = quantity;
    });
  },
};

const actions = {
  /**
   * APIを呼び出し注文内容を登録する
   * @param {*} param0
   * @param {*} state
   * @returns
   */
  register({ getters, dispatch, commit, rootGetters }) {
    return axios
      .post(environments.shoppingFrontUrl, JSON.stringify(getters['requestBodyForRegister']))
      .then((response) => {
        // console.debug('success response:' + JSON.stringify(response));
        commit('setRegisterResponse', response);
        return {
          success: true,
          status: response.status,
          message: response.data.message,
        };
      })
      .catch((error) => {
        // console.error('error error:' + JSON.stringify(error));
        return {
          success: false,
          status: error.status,
          message: error.message,
        };
      });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
